import axios from "../axios";
import assert from "utils/assert";

const API_PATH = "/issb";

export class QuestionGrouping {
  /** @type {!string} */
  groupedBy;
  /** @type {!Array<QuestionGroup>} */
  data;
}

/**
 * Data structure for a group of questions
 */
export class QuestionGroup {
  /** @type {!string} */
  groupName;
  /** @type {!Array<Question>} */
  questions;
}

const api = {
  // TODO replace with questionnaire.service.js list()
  getQuestionnaireRounds(paramsString, accessToken) {
    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios.get(`${API_PATH}/questionnaire?${paramsString}`, config).then(({ data }) => data);
  },

  /**
   * Warning: This is a destructive operation. It also deletes all the answers associated with the questionnaire round.
   * @param {string} id Questionnaire round businessId
   */
  deleteQuestionnaireRound(id) {
    assert(id && typeof id === "string", "id must be a string", id);
    return axios.delete(`${API_PATH}/questionnaire/${id}`).then(({ data }) => data);
  },

  /**
   * @returns {Promise<QuestionGrouping>} No inherent sorting
   */
  getQuestionsOfRound({ id, paramsString }) {
    assert(id, "id is required");
    return axios.get(`${API_PATH}/questionnaire/${id}/questions?${paramsString}`).then(({ data }) => data);
  },

  /**
   * @returns {Promise<QuestionGrouping>}
   */
  getAuditQuestionsOfRound({ paramsString }, accessToken) {
    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios.get(`${API_PATH}/audit/requirements?${paramsString}`, config).then(({ data }) => data);
  },

  getStagesOfRound({ id }) {
    assert(id, "id is required");
    return axios.get(`${API_PATH}/workflow-stage/by-questionnaire/${id}`).then(({ data }) => data);
  },

  /**
   * @param {WorkflowStage[]} stages
   * @returns
   */
  saveStagesOfRound(questionnaireId, stages) {
    return axios
      .post(`${API_PATH}/workflow-stage/batch?questionnaireId=${questionnaireId}`, stages)
      .then(({ data }) => data);
  },

  /**
   * @param {*} data TODO document
   */
  saveAnswer(data, accessToken) {
    const headers = {
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios.post(`${API_PATH}/answer`, data, headers).then(({ data }) => data);
  },

  /**
   * Excludes/restore an answer from the questionnaire.
   * @param data ({questionIds: uuid[], justification: String, comment: String,  exclude: boolean})
   * Note: justification is an enum from a select, comment is a free-form text field
   * @param accessToken
   * @returns {Promise<AxiosResponse<any>>}
   */
  excludeAnswer(data, accessToken) {
    const headers = {
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    console.log("excludeAnswer", data);
    return axios.post(`${API_PATH}/answer/exclude`, data, headers).then(({ data }) => data);
  },

  getOrganizationIndustries(accessToken) {
    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios.get(`${API_PATH}/industries`, config).then(({ data }) => data);
  },

  /**
   * Note: unwraps Pageable to get the array of entities
   * @returns {Promise<Entity[]>}
   */
  getOrganizationEntities() {
    return axios.get(`${API_PATH}/entity`).then(({ data }) => data.content);
  },

  /**
   * Entities for the logged in user -- used by the canAccessEntity() hook
   * @returns {Promise<Entity[]>}
   */
  getEntitiesForUser() {
    return axios.get(`${API_PATH}/entity/for_user`).then(({ data }) => data);
  },

  /**
   * @param {{id, name, sectors, subsectors, region}} data use id=new to create a new entity
   * @returns
   */
  saveEntity(data) {
    const id = data.id;
    if (!id || id === "new") {
      delete data.id;
      return axios.post(`${API_PATH}/entity`, data).then(({ data }) => data);
    }
    return axios.post(`${API_PATH}/entity/${id}`, data).then(({ data }) => data);
  },

  deleteEntity(id) {
    assert(id, "id is required");
    return axios.delete(`${API_PATH}/entity/${id}`).then(({ data }) => data);
  },

  saveAuditOfRound(data, accessToken) {
    const headers = {
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios.post(`${API_PATH}/audit`, data, headers).then(({ data }) => data);
  },

  /**
   Returns the actions available for the user to move the workflow -- E.g. approve/reject.
   Basically this will be an action(s) to move forward, and action(s) to move backward in the workflow.
   */
  getActionsOfAnswer({ id }, accessToken) {
    assert(id, "id is required");
    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios.get(`${API_PATH}/answer/action?questionId=${id}`, config).then(({ data }) => data);
  },
  /**do an approve/reject action */
  submitAction(data, accessToken) {
    const headers = {
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios.post(`${API_PATH}/answer/action`, data, headers).then(({ data }) => data);
  },

  /**
   Returns active audit by stage id
   */
  getActiveAudit({ id }, accessToken) {
    assert(id, "id is required");
    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios.get(`${API_PATH}/audit?stageId=${id}`, config).then(({ data }) => data);
  },

  /**
   Returns all audits in questionnaire
   */
  getAllAudit({ questionnaireId }, accessToken) {
    assert(questionnaireId, "id is required");
    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios.get(`${API_PATH}/audit/all?questionnaireId=${questionnaireId}`, config).then(({ data }) => data);
  },

  closeAudit(data, accessToken) {
    const headers = {
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios.post(`${API_PATH}/audit/close`, data, headers).then(({ data }) => data);
  },

  changeOwner(data, accessToken) {
    const headers = {
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios.post(`${API_PATH}/answer/assign/owner`, data, headers).then(({ data }) => data);
  },

  changeAssignee(data, accessToken) {
    return axios.post(`${API_PATH}/answer/assign/assignee`, data).then(({ data }) => data);
  },

  getConfiguration() {
    return axios.get("/public/issb/configuration").then(({ data }) => data);
  },

  createPlan(data) {
    return axios.post("/public/issb/survey", data).then(({ data }) => data);
  },

  sendPlan({ surveyId }) {
    return axios.post(`/public/issb/email/${surveyId}`, {}).then(({ data }) => data);
  },

  getMetrics(subIndustryId) {
    return axios.get(`/public/issb/metrics?subIndustryId=${subIndustryId}`).then(({ data }) => data);
  },

  /**
   Returns activities of question
   */
  getActivities({ id }) {
    assert(id, "getActivities - id is required");
    return axios.get(`${API_PATH}/answer/activity?questionId=${id}`).then(({ data }) => data);
  },

  /**
   * Set the read flag of an activity
   * @param {Activity} activity
   * @param {boolean?} unread For if you want to mark an activity as unread
   * @returns
   */
  updateActivitySetReadFlag(activity, unread) {
    let url = `${API_PATH}/answer/activity/${activity.id}/read`;
    if (unread) {
      url += "?unread=" + unread;
    }
    return axios.post(url, activity).then(({ data }) => data);
  },

  /**
   * Change additional status of answer
   * @param data {field:string, status:string} e.g. {field: "completeness", status: "HIGH"}
   * @param accessToken
   * @returns {Promise<AxiosResponse<any>>}
   */
  changeStatus(data) {
    return axios.post(`${API_PATH}/answer/status`, data).then(({ data }) => data);
  },

  /**
   *
   * @param id - round id
   * @returns {Promise<AxiosResponse<any>>}
   */
  getDashboardData({ id }) {
    assert(id, "id is required - pass in as {id:X}");
    return axios.get(`${API_PATH}/dashboard?questionnaireId=${id}`).then(({ data }) => data);
  },

  /**
   *
   * @param data
   * @param accessToken
   * @returns {Promise<AxiosResponse<any>>}
   */
  downloadDisclosure(data, accessToken) {
    return axios
      .post(`${API_PATH}/disclosure/report`, data, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        responseType: "blob", // 👈 necessary to get PDF blob
      })
      .then((response) => response.data);
  },

  /**
   * Update the users of an entity
   * @param data
   * @param accessToken
   * @returns {Promise<AxiosResponse<any>>}
   */
  updateEntityUsers(data, accessToken) {
    const headers = {
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios.post(`${API_PATH}/entity/users`, data, headers).then(({ data }) => data);
  },

  initFoundationRound() {
    return axios.post(`${API_PATH}/questionnaire/foundation/init`).then(({ data }) => data);
  },

  addYearToFoundationRound(year) {
    return axios.post(`${API_PATH}/questionnaire/foundation/add-year?year=${year}`).then(({ data }) => data);
  },

  setFoundationControllers(ownerEmails) {
    return axios.post(`${API_PATH}/questionnaire/foundation/set-controllers`, ownerEmails).then(({ data }) => data);
  },
};

export default api;
